<!--
  线性零部件阵列“到参考”字段组。

  文件定位：
  - 只负责 SOLIDWORKS 线性零部件阵列方向区块中的“到参考”UI；
  - 不持有命令会话，也不执行几何求解；
  - 父组件负责传入并保存当前方向状态。
-->
<template>
  <div class="up-to-reference-fields">
    <div class="selection-row">
      <span class="field-label">参考几何</span>
      <LinearComponentPatternReferenceList
        :items="referenceGeometryItems"
        :active="isReferenceGeometryActive"
        empty-text="未选中任何参考几何"
        :visible-rows="1"
        :show-delete-all-action="false"
        @activate="handleReferenceGeometryFocus"
        @delete-item="handleReferenceGeometryDelete"
        @delete-all="handleReferenceGeometryContextMenu"
      />
    </div>

    <div class="number-row">
      <span class="field-label">偏移距离</span>
      <n-input-number
        :value="state.offsetDistance"
        size="small"
        :precision="2"
        :step="1"
        :min="0"
        :show-button="true"
        @update:value="handleOffsetDistanceChange"
      >
        <template #suffix>mm</template>
      </n-input-number>
    </div>

    <n-radio-group
      :value="state.referenceOffsetMode"
      size="small"
      @update:value="handleReferenceOffsetModeChange"
    >
      <div class="radio-list">
        <n-radio :value="LinearComponentPatternReferenceOffsetMode.BoundingBoxCenter">
          边界框中心(B)
        </n-radio>
        <n-radio :value="LinearComponentPatternReferenceOffsetMode.SelectedReference">
          所选参考(S)
        </n-radio>
      </div>
    </n-radio-group>

    <div
      v-if="state.referenceOffsetMode === LinearComponentPatternReferenceOffsetMode.SelectedReference"
      class="selection-row"
    >
      <span class="field-label">所选参考</span>
      <LinearComponentPatternReferenceList
        :items="selectedReferenceItems"
        :active="isSelectedReferenceActive"
        empty-text="未选中任何所选参考"
        :visible-rows="1"
        :show-delete-all-action="false"
        @activate="handleSelectedReferenceFocus"
        @delete-item="handleSelectedReferenceDelete"
        @delete-all="handleSelectedReferenceContextMenu"
      />
    </div>

    <div class="up-to-reference-toggle-row">
      <n-button-group size="small">
        <n-button
          :type="isSpacingInputActive ? 'primary' : 'default'"
          :aria-pressed="isSpacingInputActive"
          title="设置间距"
          @click="toggleSetSpacing"
        >
          间距
        </n-button>
        <n-button
          :type="isInstancesInputActive ? 'primary' : 'default'"
          :aria-pressed="isInstancesInputActive"
          title="设置实例数"
          @click="toggleSetInstances"
        >
          实例数
        </n-button>
      </n-button-group>
    </div>

    <div v-if="isSpacingInputActive" class="number-row">
      <span class="field-label">间距</span>
      <n-input-number
        :value="state.spacing"
        size="small"
        :precision="2"
        :step="1"
        :min="0"
        :show-button="true"
        @update:value="handleSpacingChange"
      >
        <template #suffix>mm</template>
      </n-input-number>
    </div>

    <div v-if="isInstancesInputActive" class="number-row">
      <span class="field-label">实例数</span>
      <n-input-number
        :value="state.instances"
        size="small"
        :precision="0"
        :step="1"
        :min="1"
        :show-button="true"
        @update:value="handleInstancesChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  NButton,
  NButtonGroup,
  NInputNumber,
  NRadio,
  NRadioGroup,
} from 'naive-ui';
import { computed } from 'vue';
import LinearComponentPatternReferenceList from './LinearComponentPatternReferenceList.vue';
import {
  LinearComponentPatternReferenceOffsetMode,
  type LinearComponentPatternDirectionState,
} from '@/core/property-manager/linear-component-pattern/LinearComponentPatternPropertyTypes';

/** 组件 Props。 */
interface LinearComponentPatternUpToReferenceFieldsProps {
  /** 当前方向状态。 */
  state: LinearComponentPatternDirectionState;
  /** 参考几何是否为当前激活输入。 */
  isReferenceGeometryActive: boolean;
  /** 所选参考是否为当前激活输入。 */
  isSelectedReferenceActive: boolean;
}

const props = defineProps<LinearComponentPatternUpToReferenceFieldsProps>();

/** 参考几何列表项。 */
const referenceGeometryItems = computed(() => {
  return props.state.referenceGeometryItem ? [props.state.referenceGeometryItem] : [];
});

/** 所选参考列表项。 */
const selectedReferenceItems = computed(() => {
  return props.state.selectedReferenceItem ? [props.state.selectedReferenceItem] : [];
});

/** 当前是否显示“间距”输入。 */
const isSpacingInputActive = computed<boolean>(() => {
  //------ 线性零部件阵列-到参考参数切换 开始------
  // 旧状态可能出现两个开关都为 true/false，这里统一兜底到“间距”输入，
  // 保证 UI 始终只显示一个输入框，与 SOLIDWORKS 的互斥切换行为一致。
  if (props.state.setSpacing === props.state.setInstances) {
    return true;
  }
  return props.state.setSpacing;
  //------ 线性零部件阵列-到参考参数切换 结束------
});

/** 当前是否显示“实例数”输入。 */
const isInstancesInputActive = computed<boolean>(() => {
  return !isSpacingInputActive.value;
});

const emit = defineEmits<{
  /**
   * 当子组件中的字段发生变化时，上抛增量状态给父组件统一写入。
   */
  (e: 'update-state', patch: Partial<LinearComponentPatternDirectionState>): void;
  /**
   * 参考几何输入框获得焦点。
   */
  (e: 'focus-reference-geometry'): void;
  /**
   * 所选参考输入框获得焦点。
   */
  (e: 'focus-selected-reference'): void;
  /**
   * 右键清除参考几何输入框。
   */
  (e: 'clear-reference-geometry'): void;
  /**
   * 右键清除所选参考输入框。
   */
  (e: 'clear-selected-reference'): void;
}>();

/**
 * 统一上抛方向状态的局部补丁，避免在子组件中直接修改 props。
 *
 * @param patch 状态补丁。
 */
function emitStatePatch(patch: Partial<LinearComponentPatternDirectionState>): void {
  emit('update-state', patch);
}

/**
 * 激活参考几何输入框对应的场景引用选择。
 */
function handleReferenceGeometryFocus(): void {
  emit('focus-reference-geometry');
}

/**
 * 删除参考几何单项。
 */
function handleReferenceGeometryDelete(): void {
  emit('clear-reference-geometry');
}

/**
 * 右键清除参考几何输入框。
 */
function handleReferenceGeometryContextMenu(): void {
  emit('clear-reference-geometry');
}

/**
 * 更新偏移距离。
 *
 * @param value 输入值。
 */
function handleOffsetDistanceChange(value: number | null): void {
  emitStatePatch({ offsetDistance: value ?? 0 });
}

/**
 * 更新偏移参考类型。
 *
 * @param value 参考类型枚举值。
 */
function handleReferenceOffsetModeChange(value: string | number | boolean): void {
  if (
    value !== LinearComponentPatternReferenceOffsetMode.BoundingBoxCenter
    && value !== LinearComponentPatternReferenceOffsetMode.SelectedReference
  ) {
    return;
  }
  emitStatePatch({ referenceOffsetMode: value });
}

/**
 * 激活所选参考输入框对应的场景引用选择。
 */
function handleSelectedReferenceFocus(): void {
  emit('focus-selected-reference');
}

/**
 * 删除所选参考单项。
 */
function handleSelectedReferenceDelete(): void {
  emit('clear-selected-reference');
}

/**
 * 右键清除所选参考输入框。
 */
function handleSelectedReferenceContextMenu(): void {
  emit('clear-selected-reference');
}

/**
 * 切换是否显式设置间距。
 */
function toggleSetSpacing(): void {
  //------ 线性零部件阵列-到参考参数切换 开始------
  emitStatePatch({ setSpacing: true, setInstances: false });
  //------ 线性零部件阵列-到参考参数切换 结束------
}

/**
 * 切换是否显式设置实例数。
 */
function toggleSetInstances(): void {
  //------ 线性零部件阵列-到参考参数切换 开始------
  emitStatePatch({ setSpacing: false, setInstances: true });
  //------ 线性零部件阵列-到参考参数切换 结束------
}

/**
 * 更新间距。
 *
 * @param value 输入值。
 */
function handleSpacingChange(value: number | null): void {
  emitStatePatch({ spacing: value ?? 0 });
}

/**
 * 更新实例数。
 *
 * @param value 输入值。
 */
function handleInstancesChange(value: number | null): void {
  emitStatePatch({ instances: Math.max(1, value ?? 1) });
}
</script>

<style scoped lang="less">
.up-to-reference-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selection-row,
.number-row {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}

.field-label {
  font-size: 12px;
  color: var(--n-text-color);
  white-space: nowrap;
  text-align: right;
}

.selection-input {
  min-width: 0;
}

.active-reference-input :deep(.n-input-wrapper) {
  box-shadow: inset 3px 0 0 #18a058;
}

.selected-reference-input :deep(.n-input-wrapper) {
  box-shadow: inset 3px 0 0 #7c3aed;
}

.radio-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 58px;
}

.up-to-reference-toggle-row {
  display: flex;
  gap: 6px;
  padding-left: 58px;
}

</style>
